import React, { useState, useEffect, useContext } from 'react'
import TableComponent, { TableComponentType } from 'UI/Table'
import { IColumn } from 'typescript/interfaces/tableUi'
import { useTableComponentStyle } from 'UI/Table/style'
import {
  convertEmptyField,
  convertToPercentageFormat,
  convertToPriceFormat,
} from 'utils/formatters'
import { IFunderLenderItem, ILenderEntity } from 'typescript/interfaces/deals'
import IconButton from '@material-ui/core/IconButton'
import { ReactComponent as EditIcon } from 'assets/svg/EditIcon.svg'
import { ReactComponent as DeleteIcon } from 'assets/svg/DeleteCircleIcon.svg'
import Button from 'UI/Button/ButtonWithPreloader'
import { DEAL_LENDERS_ACCOUNT_BALANCE_PERM } from 'constants/permissions'
import { ReactComponent as InfoIcon } from 'assets/svg/InfoIcon.svg'
import Popover from '@material-ui/core/Popover'
import Typography from '@material-ui/core/Typography'
import Box from '@material-ui/core/Box'
import { makeStyles } from '@material-ui/core/styles'
import SvgIcon from '@material-ui/core/SvgIcon'
import { UserContext } from 'contexts/userContext'
import { usePermission } from './usePermission'

export const TableLenders = TableComponent as TableComponentType<ILenderEntity>
export const TableFunderLenders = TableComponent as TableComponentType<IFunderLenderItem>

interface IUseAssignLenders {
  handleEdit?: (data: ILenderEntity) => void
  handleEditFunder?: (data: IFunderLenderItem) => void
  handleDelete?: (id: number, idCompany: number) => void
  disableActions: boolean
}

const useStyles = makeStyles(theme => ({
  infoIcon: {
    fontSize: 16,
    marginLeft: theme.spacing(0.5),
    color: theme.palette.primary.main,
    cursor: 'pointer',
  },
  popover: {
    pointerEvents: 'none',
  },
  popoverContent: {
    padding: theme.spacing(1),
  },
}))

export const useAssignLenders = (props?: IUseAssignLenders) => {
  const { hasPermission } = usePermission()
  const userContext = useContext(UserContext)
  const classes = useTableComponentStyle()
  const popoverClasses = useStyles()
  const { disableActions } = props || {}
  const [columns, setColumns] = useState<IColumn<ILenderEntity>[]>([])
  const [columnsFunder, setColumnsFunder] = useState<IColumn<IFunderLenderItem>[]>([])
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [selectedSyndicator, setSelectedSyndicator] = useState<ILenderEntity | null>(null)

  const handleInfoClick = (event: React.MouseEvent<SVGSVGElement>, syndicator: ILenderEntity) => {
    setAnchorEl(event.currentTarget.parentElement)
    setSelectedSyndicator(syndicator)
  }

  const handleInfoClose = () => {
    setAnchorEl(null)
    setSelectedSyndicator(null)
  }

  const open = Boolean(anchorEl)

  useEffect(() => {
    setColumns([
      {
        label: 'Lender #',
        field: 'id',
        bodyCellClassName: classes.secondaryBodyCell,
      },
      {
        label: 'Biz Name',
        field: 'businessName',
        bodyCellClassName: classes.accentCell,
        render: (data: ILenderEntity) => (
          <Box display="flex" alignItems="center">
            <span>{data.businessName}</span>
            {hasPermission(DEAL_LENDERS_ACCOUNT_BALANCE_PERM) && (
              <SvgIcon
                className={popoverClasses.infoIcon}
                onClick={e => handleInfoClick(e, data)}
                onMouseEnter={e => handleInfoClick(e, data)}
                onMouseLeave={handleInfoClose}
              >
                <InfoIcon />
              </SvgIcon>
            )}
          </Box>
        ),
      },
      {
        label: 'Main Lender',
        field: 'isLead',
        bodyCellClassName: classes.accentCell,
        render: data => (data.isLead ? 'Yes' : 'No'),
      },
      {
        label: 'Amt Participated',
        field: 'amount',
        bodyCellClassName: classes.accentCell,
        render: data => convertToPriceFormat(data.lendingAmount),
      },
      {
        label: 'Percentage',
        field: 'percentage',
        sorting: true,
        bodyCellClassName: classes.accentCell,
        render: data => convertToPercentageFormat(data.lendingPercentage),
      },
      ...(userContext.state.roles.isAdmin
        ? [
            {
              label: 'Account Balance',
              field: 'availableBalance',
              bodyCellClassName: classes.accentCell,
              render: (data: ILenderEntity) => convertToPriceFormat(data.availableBalance || 0),
            },
          ]
        : []),
      {
        label: 'Edit',
        field: 'edit',
        render: data => {
          return (
            <IconButton
              disabled={disableActions}
              onClick={() => props?.handleEdit && props?.handleEdit(data)}
            >
              <EditIcon />
            </IconButton>
          )
        },
      },
      {
        label: 'Delete',
        field: 'delete',
        render: data => {
          return (
            <IconButton
              disabled={disableActions}
              onClick={() =>
                props?.handleDelete &&
                props?.handleDelete(Number(data.id), Number(data.syndicatorId))
              }
            >
              <DeleteIcon />
            </IconButton>
          )
        },
      },
    ])

    setColumnsFunder([
      {
        label: 'Syndicator #',
        field: 'syndicator',
        bodyCellClassName: classes.secondaryBodyCell,
        render: data => data.syndicatorId,
      },
      {
        label: 'Biz Name',
        field: 'bussName',
        bodyCellClassName: classes.secondaryBodyCell,
        render: data => convertEmptyField(data.businessName),
      },
      {
        label: 'First Name',
        field: 'firstName',
        bodyCellClassName: classes.secondaryBodyCell,
        render: data => convertEmptyField(data.firstName),
      },
      {
        label: 'Last Name',
        field: 'lastName',
        bodyCellClassName: classes.secondaryBodyCell,
        render: data => convertEmptyField(data.lastName),
      },
      {
        label: 'Main Lender',
        field: 'isMain',
        bodyCellClassName: classes.secondaryBodyCell,
        render: data => (data.isLead ? 'Yes' : 'No'),
      },
      {
        label: 'Amt Participated',
        field: 'amount',
        bodyCellClassName: classes.secondaryBodyCell,
        render: data => convertToPriceFormat(data.lendingAmount),
      },
      {
        label: 'Amt Allowed',
        field: 'amountAllowed',
        bodyCellClassName: classes.secondaryBodyCell,
        render: data => convertToPriceFormat(data.limit),
      },
      {
        label: 'Percentage',
        field: 'percentage',
        bodyCellClassName: classes.secondaryBodyCell,
        render: data => convertToPercentageFormat(data.lendingPercentage),
      },
      {
        label: '',
        field: 'actions',
        render: data => (
          <Button
            color="secondary"
            variant="outlined"
            size="small"
            disabled={disableActions}
            onClick={() => props?.handleEditFunder && props?.handleEditFunder(data)}
          >
            {data.isAttachedToDeal ? 'Edit' : 'Assign'}
          </Button>
        ),
      },
    ])
  }, [disableActions])

  return {
    columns: columns,
    columnsFunder,
    popover: (
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleInfoClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        className={popoverClasses.popover}
      >
        {selectedSyndicator && (
          <Box className={popoverClasses.popoverContent}>
            <Typography variant="body2">
              <strong>Available Balance:</strong>{' '}
              {convertToPriceFormat(selectedSyndicator.availableBalance || 0)}
            </Typography>
          </Box>
        )}
      </Popover>
    ),
  }
}
